// NOAA solar position, the closed form. Never approximate sunset.
// Ported from the script used on the design boards; returns local clock time for Europe/London.
const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** Minutes after local midnight (UTC + offset) for sunrise and sunset. */
export function sunTimes(lat: number, lon: number, isoDate: string): { rise: number; set: number; offsetMin: number } {
  const [y, m, d] = isoDate.split("-").map(Number);
  const day = Date.UTC(y, m - 1, d) / 86400000;          // days since epoch
  const n = day - 10957 + 0.5;                            // days since 2000-01-01, at noon
  const T = n / 36525;
  const L0 = (280.46646 + T * (36000.76983 + 0.0003032 * T)) % 360;
  const M = 357.52911 + T * (35999.05029 - 0.0001537 * T);
  const e = 0.016708634 - T * (0.000042037 + 0.0000001267 * T);
  const Mr = rad(M);
  const C = Math.sin(Mr) * (1.914602 - T * (0.004817 + 0.000014 * T)) + Math.sin(2 * Mr) * (0.019993 - 0.000101 * T) + Math.sin(3 * Mr) * 0.000289;
  const om = 125.04 - 1934.136 * T;
  const lam = L0 + C - 0.00569 - 0.00478 * Math.sin(rad(om));
  const eps0 = 23 + (26 + (21.448 - T * (46.815 + T * (0.00059 - T * 0.001813))) / 60) / 60;
  const eps = eps0 + 0.00256 * Math.cos(rad(om));
  const dec = deg(Math.asin(Math.sin(rad(eps)) * Math.sin(rad(lam))));
  const yv = Math.tan(rad(eps / 2)) ** 2;
  const L0r = rad(L0);
  const eqt = 4 * deg(yv * Math.sin(2 * L0r) - 2 * e * Math.sin(Mr) + 4 * e * yv * Math.sin(Mr) * Math.cos(2 * L0r) - 0.5 * yv * yv * Math.sin(4 * L0r) - 1.25 * e * e * Math.sin(2 * Mr));
  const ha = deg(Math.acos(Math.cos(rad(90.833)) / (Math.cos(rad(lat)) * Math.cos(rad(dec))) - Math.tan(rad(lat)) * Math.tan(rad(dec))));
  const offsetMin = londonOffsetMin(isoDate);
  const noon = 720 - 4 * lon - eqt + offsetMin;
  return { rise: noon - 4 * ha, set: noon + 4 * ha, offsetMin };
}

/** BST runs from 01:00 UTC on the last Sunday of March to 01:00 UTC on the last Sunday of October. */
export function londonOffsetMin(isoDate: string): number {
  const [y, m, d] = isoDate.split("-").map(Number);
  const lastSunday = (month: number) => {
    const last = new Date(Date.UTC(y, month, 0));
    return last.getUTCDate() - last.getUTCDay();
  };
  const start = Date.UTC(y, 2, lastSunday(3));
  const end = Date.UTC(y, 9, lastSunday(10));
  const t = Date.UTC(y, m - 1, d);
  return t >= start && t < end ? 60 : 0;
}

export const hhmm = (min: number) => {
  const r = Math.round(min);
  return `${String(Math.floor(r / 60)).padStart(2, "0")}:${String(r % 60).padStart(2, "0")}`;
};
export const toMin = (s: string) => {
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};
